"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar-lazy";
import { Label } from "@/components/ui/label";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import React from "react";
import { FormStoryblok, FormFieldStoryblok } from "@/component-types-sb";
import Title from "@/components/Title";
import { storyblokEditable } from "@storyblok/react/rsc";
import {
  fieldKeys,
  isPdf,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_FILES_PER_FIELD,
} from "@/lib/formFields";
import FormGuard from "@/components/FormGuard";
import { useIsModal } from "@/components/provider/ModalProvider";

const INPUT_TYPE_BY_FIELD_TYPE: Record<string, string> = {
  text: "text",
  email: "email",
  number: "number",
  time: "time",
};

const validateFiles = (files: FileList | null): string | undefined => {
  if (!files || files.length === 0) return undefined;
  if (files.length > MAX_FILES_PER_FIELD) {
    return `Maximal ${MAX_FILES_PER_FIELD} Dateien erlaubt.`;
  }
  for (const file of Array.from(files)) {
    if (!isPdf(file)) {
      return "Nur PDF-Dateien sind erlaubt.";
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `Dateien dürfen maximal ${MAX_FILE_SIZE_MB} MB gross sein.`;
    }
  }
  return undefined;
};

// Format date in German, matching Button type="reserve"
const formatDate = (date: Date) =>
  date.toLocaleDateString("de-DE", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const defaultDateValues = (fields: FormFieldStoryblok[], keys: string[]) => {
  const defaults: Record<string, Date | undefined> = {};
  fields.forEach((field, index) => {
    if (field.type === "date") {
      defaults[keys[index]] = new Date();
    }
  });
  return defaults;
};

const Form = ({ blok }: Readonly<{ blok: FormStoryblok }>) => {
  const isModal = useIsModal();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [fileErrors, setFileErrors] = React.useState<Record<string, string>>(
    {}
  );

  const fields = React.useMemo(() => blok.fields || [], [blok.fields]);
  const keys = React.useMemo(() => fieldKeys(fields), [fields]);

  const handleFileChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const validationError = validateFiles(e.target.files);
      if (validationError) {
        // Reject the whole selection rather than silently dropping invalid
        // files — surfacing the problem beats guessing what the user meant.
        e.target.value = "";
      }
      setFileErrors((prev) => {
        if (!validationError) {
          if (!(key in prev)) return prev;
          const rest = { ...prev };
          delete rest[key];
          return rest;
        }
        return { ...prev, [key]: validationError };
      });
    };

  const [dateValues, setDateValues] = React.useState<
    Record<string, Date | undefined>
  >(() => defaultDateValues(fields, keys));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(null);
    setError(null);

    const formData = new FormData(e.currentTarget);

    for (const [index, field] of fields.entries()) {
      const key = keys[index];
      if (field.type === "date" && field.required && !dateValues[key]) {
        setError("Bitte wählen Sie ein Datum aus.");
        setIsSubmitting(false);
        return;
      }
      if (field.type === "file") {
        const files = formData
          .getAll(key)
          .filter((value): value is File => value instanceof File && value.size > 0);
        if (fileErrors[key]) {
          setError(fileErrors[key]);
          setIsSubmitting(false);
          return;
        }
        if (field.required && files.length === 0) {
          setError(`Bitte wählen Sie eine Datei für "${field.name}" aus.`);
          setIsSubmitting(false);
          return;
        }
      }
    }

    // Values only, by field key: the server reads this form from Storyblok
    // (page and block id) for labels, recipient and subject, and writes the mail.
    const payload = new FormData();
    payload.append("kind", "form");
    payload.append("page", window.location.pathname);
    payload.append("form", blok._uid);
    for (const name of ["website_url", "form_fill_ms"]) {
      payload.append(name, (formData.get(name) as string | null) ?? "");
    }
    fields.forEach((field, index) => {
      const key = keys[index];
      if (field.type === "date") {
        payload.append(key, dateValues[key]?.toISOString() ?? "");
      } else if (field.type === "file") {
        formData
          .getAll(key)
          .filter((value): value is File => value instanceof File && value.size > 0)
          .forEach((file) => payload.append(key, file));
      } else {
        const value = formData.get(key);
        payload.append(key, typeof value === "string" ? value : "");
      }
    });

    try {
      const emailResponse = await fetch("/api/sendEmail", {
        method: "POST",
        body: payload,
      });

      const result = await emailResponse.json().catch(() => ({}));

      if (!emailResponse.ok) {
        throw new Error(result.message || "Es gab ein Problem beim Senden der E-Mail.");
      }

      setSuccess(
        blok.message_success || "Ihre Nachricht wurde erfolgreich übermittelt!"
      );
      (e.target as HTMLFormElement).reset();
      setDateValues(defaultDateValues(fields, keys));
      setFileErrors({});
    } catch (err: unknown) {
      setError(
        blok.message_error ||
          (err instanceof Error
            ? err.message ||
              "Es gab ein Problem bei der Übermittlung. Bitte versuchen Sie es erneut."
            : "Unbekannter Fehler")
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      {...storyblokEditable(blok)}
      className={`flex justify-center flex-col items-center ${isModal ? "" : "pb-10"}`}
    >
      {isModal ? (
        (blok.title || blok.description) && (
          <DialogHeader className="w-full mb-4">
            {blok.title && <DialogTitle>{blok.title}</DialogTitle>}
            {blok.description && (
              <DialogDescription>{blok.description}</DialogDescription>
            )}
          </DialogHeader>
        )
      ) : (
        <>
          {blok.title && (
            <Title
              variant="h3"
              className="text-2xl font-bold mb-4 text-left lg:text-center w-full lg:max-w-4xl"
            >
              {blok.title}
            </Title>
          )}
          {blok.description && (
            <p className="mb-8 text-left lg:text-center w-full lg:max-w-2xl">
              {blok.description}
            </p>
          )}
        </>
      )}
      {success ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 lg:max-w-2xl">
          <p className="text-green-800">{success}</p>
        </div>
      ) : (
        <form
          className="grid gap-4 w-full lg:max-w-2xl mt-4"
          onSubmit={handleSubmit}
        >
          {fields.map((field, index) => {
            const key = keys[index];
            const id = `${blok._uid}-${key}`;
            const label = `${field.name}${field.required ? " *" : ""}`;

            return (
              <div key={field._uid} className="grid gap-2">
                <Label htmlFor={id}>{label}</Label>
                {field.type === "richtext" && (
                  <Textarea
                    id={id}
                    name={key}
                    placeholder={field.name}
                    rows={3}
                    className="resize-none"
                    required={field.required}
                  />
                )}
                {field.type === "time" && (
                  <Input
                    id={id}
                    name={key}
                    type="time"
                    placeholder={field.name}
                    defaultValue="18:00"
                    required={field.required}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                  />
                )}
                {field.type === "date" && (
                  <>
                    <Input
                      id={id}
                      className="w-full justify-start text-left font-normal"
                      value={
                        dateValues[key]
                          ? formatDate(dateValues[key]!)
                          : "Datum wählen"
                      }
                      readOnly
                    />
                    <Calendar
                      mode="single"
                      selected={dateValues[key]}
                      onSelect={(selectedDate) =>
                        setDateValues((prev) => ({
                          ...prev,
                          [key]: selectedDate,
                        }))
                      }
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      formatters={{
                        formatWeekdayName: (date) =>
                          date.toLocaleDateString("de-DE", {
                            weekday: "short",
                          }),
                        formatMonthCaption: (date) =>
                          date.toLocaleDateString("de-DE", {
                            month: "long",
                            year: "numeric",
                          }),
                      }}
                    />
                  </>
                )}
                {field.type === "file" && (
                  <>
                    <Input
                      id={id}
                      name={key}
                      type="file"
                      accept=".pdf,application/pdf"
                      multiple
                      required={field.required}
                      onChange={handleFileChange(key)}
                      className="cursor-pointer file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                    />
                    <p className="text-xs text-muted-foreground">
                      PDF, max. {MAX_FILE_SIZE_MB} MB pro Datei, max.{" "}
                      {MAX_FILES_PER_FIELD} Dateien.
                    </p>
                    {fileErrors[key] && (
                      <p className="text-sm text-red-600">
                        {fileErrors[key]}
                      </p>
                    )}
                  </>
                )}
                {field.type !== "richtext" &&
                  field.type !== "time" &&
                  field.type !== "date" &&
                  field.type !== "file" && (
                    <Input
                      id={id}
                      name={key}
                      type={
                        INPUT_TYPE_BY_FIELD_TYPE[field.type || "text"] ||
                        "text"
                      }
                      placeholder={field.name}
                      required={field.required}
                    />
                  )}
              </div>
            );
          })}

          <FormGuard />

          <Button type="submit" variant="default" disabled={isSubmitting}>
            {isSubmitting
              ? "Wird gesendet..."
              : blok.submit_text || "Abschicken"}
          </Button>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default Form;
